// src/pages/Admin/Disciplines/EditDisciplineModal.jsx
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaSave,
  FaTimes,
  FaImage,
  FaListOl,
  FaCloudUploadAlt,
  FaLink,
  FaCrop,
} from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { updateDiscipline } from "../../../services/disciplinesServices";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  uploadDisciplinePhoto,
  cropDisciplinePhoto,
  toAbsolutePhotoUrl as toAbsDisciplinePhoto,
} from "../../../services/disciplinePhotoServices";
import CropPhotoModal, { toMasterUrl } from "../../../components/CropPhotoModal/CropPhotoModal";

/* Quill */
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};
const formats = [
  "header","font","size","bold","italic","underline","strike","blockquote",
  "color","background","script","list","bullet","indent","link","image","video",
];

/* helper cache-busting sûr */
const withBust = (url, v) => {
  if (!url) return url;
  return url + (url.includes("?") ? "&" : "?") + "v=" + v;
};

const EditDisciplineModal = ({ isActive, onClose, refreshDisciplines, disciplineData }) => {
  const [formData, setFormData] = useState({
    nom_discipline: "",
    description_discipline: "",
    image_discipline: "",
    contenu_discipline: "",
    ordre_discipline: "",
  });

  const defaultImg = "/img/no-image.jpg";
  const [imgVersion, setImgVersion] = useState(0);
  const [imgOk, setImgOk] = useState(true);

  // Upload local
  const [uploadMode, setUploadMode] = useState("url"); // 'url' | 'upload'
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp");
  const fileInputRef = useRef(null);

  // Crop
  const [isCropOpen, setIsCropOpen] = useState(false);

  const dialogRef = useRef(null);

  useEffect(() => {
    if (!disciplineData) return;
    const data = {
      nom_discipline: disciplineData.nom_discipline || "",
      description_discipline: disciplineData.description_discipline || "",
      image_discipline: disciplineData.image_discipline || "",
      contenu_discipline: disciplineData.contenu_discipline || "",
      ordre_discipline: disciplineData.ordre_discipline ?? "",
    };
    setFormData(data);
    setUploadMode("url");
    setUploadProgress(0);
    setIsUploading(false);
    setImgOk(true);
    setImgVersion((v) => v + 1);
  }, [disciplineData]);

  useEffect(() => {
    if (!isActive) return;
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";
    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='nom_discipline']")?.focus();
    }, 0);
    const onEsc = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onEsc);
    return () => {
      root.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onEsc);
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const handleClose = () => onClose?.();
  const handleOverlayClick = (e) => { if (e.target === e.currentTarget) handleClose(); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "image_discipline") {
      setImgVersion((v) => v + 1);
      setImgOk(true);
    }
  };

  const openFilePicker = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (!file) return;
    if (!disciplineData?.id) {
      toast.error("ID discipline manquant.");
      return;
    }
    const okType = /^image\/(png|jpe?g|webp)$/i.test(file.type);
    if (!okType) {
      toast.error("Format d'image non supporté. Utilisez PNG / JPG / WEBP.");
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      e.target.value = "";
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      const res = await uploadDisciplinePhoto(disciplineData.id, file, {
        format: outputFormat,
        onUploadProgress: (pe) => {
          if (!pe.total) return;
          const percent = Math.round((pe.loaded * 100) / pe.total);
          setUploadProgress(percent);
        },
      });
      const newUrl = res.image_discipline || res.photoURL || "";
      setFormData((prev) => ({ ...prev, image_discipline: newUrl }));
      setImgOk(true);
      setImgVersion((v) => v + 1);
      toast.success("Image uploadée et traitée (400×400).");
    } catch (err) {
      console.error(err);
      toast.error(`Erreur upload: ${err?.message || "inconnue"}`);
    } finally {
      setIsUploading(false);
    }
  };

  const buildUpdatePayload = () => {
    const base = {
      nom_discipline: (formData.nom_discipline || "").trim(),
      description_discipline: (formData.description_discipline || "").trim(),
      contenu_discipline: formData.contenu_discipline || "",
      ordre_discipline:
        String(formData.ordre_discipline || "").trim() === ""
          ? 0
          : Number(formData.ordre_discipline),
    };

    // ⚠️ NE PAS ÉCRASER l'image si on est en mode "upload" (le PATCH /photo s'en charge)
    // En mode URL, n'envoyer que si non vide → évite de passer "" qui effacerait la DB.
    if (uploadMode === "url") {
      const img = (formData.image_discipline || "").trim();
      if (img) {
        return { ...base, image_discipline: img };
      }
      return base;
    }

    return base;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isUploading) {
        toast.info("Patiente, l'upload est en cours…");
        return;
      }
      const payload = buildUpdatePayload();

      if (!payload.nom_discipline) throw new Error("Le nom est requis.");
      if (!payload.description_discipline) throw new Error("La description est requise.");
      if (!Number.isFinite(payload.ordre_discipline)) throw new Error("L'ordre d'affichage doit être un nombre.");

      await updateDiscipline(disciplineData.id, payload);
      toast.success("Discipline modifiée avec succès");
      handleClose();
      refreshDisciplines?.();
    } catch (error) {
      console.error("Erreur lors de la modification de la discipline", error);
      toast.error(error?.message || "Erreur lors de la modification de la discipline");
    }
  };

  if (!isActive) return null;

  // URL absolue pour l’aperçu
  const absolutePhoto = formData.image_discipline
    ? toAbsDisciplinePhoto(formData.image_discipline)
    : defaultImg;
  const imgSrc = withBust(absolutePhoto || defaultImg, imgVersion);

  // Pour la modale de crop : disponible si image sur le serveur (/uploads/…)
  const canCrop = !!formData.image_discipline && formData.image_discipline.startsWith("/uploads/");
  const masterForCrop = canCrop
    ? withBust(toAbsDisciplinePhoto(toMasterUrl(formData.image_discipline)), imgVersion)
    : "";

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Modifier une discipline"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-4xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh] sm:max-h-[92dvh]"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
          <div>
            <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
              Modifier Discipline
            </h3>
            <p className="text-white/90 text-xs sm:text-sm">
              Mettez à jour nom, description, image, contenu et ordre d’affichage.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
            aria-label="Fermer"
            type="button"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-4 sm:gap-6">
            {/* Image + URL/Upload */}
            <div className="flex md:flex-col gap-4 md:gap-5">
              <div className="relative self-start">
                <img
                  src={imgSrc}
                  alt="Aperçu"
                  onError={(e) => {
                    e.currentTarget.src = defaultImg;
                    setImgOk(false);
                  }}
                  className="h-24 w-24 sm:h-28 sm:w-28 rounded-xl object-cover shadow ring-2 ring-white dark:ring-slate-800"
                />
                <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center h-7 w-7 rounded-full bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 shadow ring-2 ring-white dark:ring-slate-900">
                  <FaImage className="text-[12px]" />
                </span>
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-4 mb-2">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="photoMode"
                      value="url"
                      checked={uploadMode === "url"}
                      onChange={() => setUploadMode("url")}
                    />
                    <span className="flex items-center gap-2 text-sm">
                      <FaLink /> Lien URL
                    </span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="photoMode"
                      value="upload"
                      checked={uploadMode === "upload"}
                      onChange={() => setUploadMode("upload")}
                    />
                    <span className="flex items-center gap-2 text-sm">
                      <FaCloudUploadAlt /> Uploader
                    </span>
                  </label>
                </div>

                {uploadMode === "url" ? (
                  <>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      URL de l’image
                    </label>
                    <div className="mt-1 relative">
                      <input
                        type="url"
                        name="image_discipline"
                        value={formData.image_discipline}
                        onChange={handleChange}
                        placeholder="https://…/image.jpg ou /uploads/photos_disciplines/…"
                        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                        inputMode="url"
                      />
                    </div>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      PNG/JPG/WebP recommandé. <br />
                      Taille carrée conseillée (512×512).
                    </p>
                  </>
                ) : (
                  <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white/70 dark:bg-slate-900/30">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />

                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          className=" bg-primary rounded-xl gap-2 px-3 py-1.5 text-sm text-white hover:bg-primary-dark flex items-center transition ring-1 ring-primary/70 hover:ring-primary"
                          onClick={openFilePicker}
                        >
                          Choisir un fichier
                        </button>

                        <div className="flex items-center gap-2">
                          <span className="text-sm">Format</span>
                          <select
                            className="select select-bordered select-sm"
                            value={outputFormat}
                            onChange={(e) => setOutputFormat(e.target.value)}
                            disabled={isUploading}
                          >
                            <option value="webp">WEBP (recommandé)</option>
                            <option value="jpg">JPG</option>
                            <option value="png">PNG</option>
                          </select>
                        </div>
                      </div>

                      {isUploading && (
                        <div>
                          <progress className="progress w-full" value={uploadProgress} max="100"></progress>
                          <div className="text-xs mt-1">{uploadProgress}%</div>
                        </div>
                      )}

                      <p className="text-xs opacity-70">
                        L’image sera traitée côté serveur en carré 400×400 et optimisée.
                      </p>
                    </div>
                  </div>
                )}

                {/* Aperçu + Crop */}
                <div className="mt-3">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <FaImage />
                    <span>Aperçu</span>
                  </div>

                  <div
                    className="mt-2 h-28 w-28 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center"
                    onClick={(e) => { if (uploadMode === "upload") openFilePicker(e); }}
                  >
                    {formData.image_discipline ? (
                      <img
                        src={imgSrc}
                        alt="Aperçu"
                        onError={() => setImgOk(false)}
                        className="h-full w-full object-cover pointer-events-none"
                      />
                    ) : (
                      <span className="text-xs text-slate-500 px-2 text-center">Aucun visuel</span>
                    )}
                  </div>

                  {canCrop ? (
                    <button
                      type="button"
                      className="mt-2 inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs ring-1 ring-slate-200 dark:ring-slate-700 bg-slate-100 dark:bg-slate-800"
                      onClick={() => setIsCropOpen(true)}
                      title="Recadrer / Zoom"
                    >
                      <FaCrop /> Recadrer / Zoom
                    </button>
                  ) : formData.image_discipline ? (
                    <p className="mt-2 text-xs text-slate-500">
                      Le recadrage est disponible pour les images
                      <br />hébergées sur le serveur <br />
                      (chemins commençant par <code>/uploads/</code>).<br />
                      Uploade l’image pour activer le crop.
                    </p>
                  ) : null}

                  {!imgOk && (
                    <div className="mt-2 text-xs text-rose-600">
                      Impossible de charger l’image.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Champs principaux */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Nom de la discipline <span className="text-pink-600">*</span>
                </label>
                <input
                  type="text"
                  name="nom_discipline"
                  value={formData.nom_discipline}
                  onChange={handleChange}
                  required
                  placeholder="Ex : Judo, Ju-jitsu, Taïso…"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Description <span className="text-pink-600">*</span>
                </label>
                <input
                  type="text"
                  name="description_discipline"
                  value={formData.description_discipline}
                  onChange={handleChange}
                  required
                  placeholder="Courte description"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
                  Contenu (présentation détaillée)
                </label>
                <div className="[&_.ql-toolbar]:rounded-t-xl [&_.ql-toolbar]:border-slate-300 dark:[&_.ql-toolbar]:border-slate-700
                                [&_.ql-toolbar]:bg-white/80 dark:[&_.ql-toolbar]:bg-slate-900/70
                                [&_.ql-container]:rounded-b-xl [&_.ql-container]:border-slate-300 dark:[&_.ql-container]:border-slate-700
                                [&_.ql-container]:bg-white/80 dark:[&_.ql-container]:bg-slate-900/70">
                  <ReactQuill
                    value={formData.contenu_discipline}
                    onChange={(v) => setFormData((p) => ({ ...p, contenu_discipline: v }))}
                    theme="snow"
                    placeholder="Décrivez la discipline, ses objectifs, ses bienfaits…"
                    modules={modules}
                    formats={formats}
                    className="min-h-[180px]"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Ordre d’affichage <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaListOl />
                  </span>
                  <input
                    type="number"
                    name="ordre_discipline"
                    value={formData.ordre_discipline}
                    onChange={handleChange}
                    required
                    min="0"
                    step="1"
                    placeholder="0"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 mt-6">
            <div className="pointer-events-none h-6 bg-gradient-to-t from-white/95 dark:from-slate-900/95 to-transparent" />
            <div className="pointer-events-auto bg-white/95 dark:bg-slate-900/95 border-t border-slate-200/70 dark:border-slate-800/70 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
              <Button
                text="Annuler"
                icon={FaTimes}
                onClick={handleClose}
                type="button"
                className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
              />
              <Button
                text="Enregistrer"
                icon={FaSave}
                type="submit"
                className="!rounded-xl gap-2"
                disabled={isUploading}
              />
            </div>
          </div>
        </form>
      </div>

      {/* Crop modal (uniquement pour /uploads/...) */}
      <CropPhotoModal
        isOpen={isCropOpen}
        onClose={() => setIsCropOpen(false)}
        entityId={disciplineData?.id}
        imageUrl={masterForCrop}
        onCropped={(newUrl) => {
          setFormData((p) => ({ ...p, image_discipline: newUrl }));
          setImgVersion((v) => v + 1);
        }}
        format="webp"
        cropAction={cropDisciplinePhoto}
      />

      <style>{`
        input::placeholder { color: #94a3b8; }
        .dark input::placeholder { color: #94a3b8; opacity: .7; }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar { width: 10px; }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.5);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }
        .dark .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb { background: rgba(148,163,184,.35); }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-track { background: transparent; }
      `}</style>
    </div>,
    document.body
  );
};

export default EditDisciplineModal;
